// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Checkbox, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { type JSX, useState } from 'react';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';

interface CancelInviteModalProps {
  opened: boolean;
  onClose: () => void;
  practitioner: {
    id: string;
    name: string;
    email: string;
  };
  onSuccess: () => void;
}

/**
 * Confirmation modal for cancelling a pending provider invite.
 *
 * Requires the user to check an acknowledgement checkbox before the
 * destructive action can be submitted. On success, the Staffing table
 * is refreshed to reflect the removal.
 */
export function CancelInviteModal({ opened, onClose, practitioner, onSuccess }: CancelInviteModalProps): JSX.Element {
  const medplum = useMedplum();
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClose = (): void => {
    setConfirmed(false);
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    if (!confirmed) {
      return;
    }
    setLoading(true);
    try {
      await medplum.executeBot(botIdentifier('cancel-provider-invite'), {
        practitionerReference: `Practitioner/${practitioner.id}`,
      });
      notifications.show({
        color: 'green',
        title: 'Invite cancelled',
        message: `The invite for ${practitioner.name} has been cancelled and their profile has been removed.`,
      });
      setConfirmed(false);
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Cancel provider invite"
      size="md"
      data-testid="cancel-invite-modal"
    >
      <Stack gap="md">
        <Text size="sm">
          You are about to cancel the pending invite for{' '}
          <Text component="span" fw={600}>
            {practitioner.name}
          </Text>{' '}
          (
          <Text component="span" fs="italic">
            {practitioner.email}
          </Text>
          ).
        </Text>
        <Text size="sm">
          This will permanently delete the provider profile, role, and project membership. The User account stays
          (Medplum cloud limitation — no super-admin access). Re-inviting the same email will reuse that User and
          trigger a fresh welcome email.
        </Text>
        <Checkbox
          label="I understand this action is irreversible"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.currentTarget.checked)}
          data-testid="cancel-invite-confirm-checkbox"
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={loading} data-testid="cancel-invite-keep-button">
            Keep invite
          </Button>
          <Button
            color="red"
            disabled={!confirmed}
            loading={loading}
            onClick={() => {
              handleConfirm().catch(console.error);
            }}
            data-testid="cancel-invite-confirm-button"
          >
            Cancel invite
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
